import {
  Transforms,
  Path,
  Node,
  Editor,
  Range
} from 'slate';

import {
  ReactEditor
} from 'slate-react';

import elementTypes from '../../../elements/types';
import HelptapEditor from '../../';
import * as baseNodeUtilities from '../base-node';

export function getDefaultOptions() {

  const options = baseNodeUtilities.getDefaultOptions();

  return options;

}

export function buildNode(options = {}) {

  const node = {
    ...getDefaultOptions(),
    ...options,
    type: elementTypes.TOGGLE_LIST_ITEM,
    properties: {},
    children: [{
      properties: {
        collapsed: true
      },
      type: elementTypes.TOGGLE_LIST_HEAD,
      children: [{ text: '' }]
    }, {
      type: elementTypes.TOGGLE_LIST_BODY,
      properties: {
        collapsed: true
      },
      children: [HelptapEditor.paragraph.buildNode()]
    }]
  };

  return node;

}

export function toggleCollapsed(editor, nodePath) {

  const bodyNodePath = Path.next(nodePath);

  const body = Node.get(editor, bodyNodePath);

  const properties = {
    ...body.properties,
    collapsed: body.properties.collapsed === false
  };

  if (properties.collapsed) {
    const headNode = Node.get(editor, nodePath);
    const textNodePath = [...nodePath, headNode.children.length - 1];
    Transforms.select(editor, {
      anchor: { path: textNodePath, offset: headNode.children[headNode.children.length - 1].text.length },
      focus: { path: textNodePath, offset: headNode.children[headNode.children.length - 1].text.length }
    });
  }

  Transforms.setNodes(editor, { properties }, { at: nodePath });
  Transforms.setNodes(editor, { properties }, { at: bodyNodePath });

}

function doInsert(editor, path) {

  const nodeOptions = buildNode();

  HelptapEditor.insertNode(editor, nodeOptions, path);
  select(editor, path);

}

export function insert(editor, path) {

  Editor.withoutNormalizing(editor, () => {

    doInsert(editor, path);

  });

}

export function getClosestNodePathByType(editor, type, path) {

  while (path.length > 0) {
    const currentNode = Node.get(editor, path);
    if (currentNode.type === type) {
      return path;
    }
    path = Path.parent(path);
  }

  return null;

}

export function getClosestToggleNodePath(editor, path) {

  return getClosestNodePathByType(editor, elementTypes.TOGGLE_LIST_ITEM, path);

}

export function getClosestHeadNodePath(editor, path) {

  return getClosestNodePathByType(editor, elementTypes.TOGGLE_LIST_HEAD, path);

}

export function getClosestBodyNodePath(editor, path) {

  return getClosestNodePathByType(editor, elementTypes.TOGGLE_LIST_BODY, path);

}

export function isHeadDescendantSelected(editor) {

  const nodeEntry = Editor.above(editor, {
    at: editor.selection,
    match: n => {
      return n.type === elementTypes.TOGGLE_LIST_HEAD;
    }
  });

  return !!nodeEntry;

}

export function handleEnterKeyOnHead(editor) {

  const listItemNodePath = getClosestToggleNodePath(editor, editor.selection.anchor.path);
  const headNodePath = getClosestHeadNodePath(editor, editor.selection.anchor.path);
  const headNode = Node.get(editor, headNodePath);
  const empty = HelptapEditor.isEmptyNode(headNode);

  if (empty === true) {
    Transforms.removeNodes(editor, { at: listItemNodePath });
    HelptapEditor.paragraph.insert(editor, listItemNodePath);
  } else {
    insert(editor, Path.next(listItemNodePath));
  }

  return true;

}

export function select(editor, listItemNodePath) {

  const headNodePath = [...listItemNodePath, 0];
  const headNode = Node.get(editor, headNodePath);

  const endOfHeadNodePath = [...listItemNodePath, 0, headNode.children.length - 1];

  Transforms.select(editor, endOfHeadNodePath);
  Transforms.collapse(editor, { edge: 'end' });

}

export function handleDeleteBackward(editor, topLevelNode, topLevelNodePath) {

  const empty = HelptapEditor.isEmptyV2(editor, topLevelNode);

  if (empty === false) {
    const listItemNodePath = Path.parent(topLevelNodePath);
    const listItemNode = Node.get(editor, listItemNodePath);
    const bodyNode = listItemNode.children[1];
    if (editor.selection.focus.offset === 0 && HelptapEditor.isEmptyV2(editor, bodyNode) === false) {
      Transforms.removeNodes(editor, { at: listItemNodePath });
      const content = structuredClone(bodyNode.children);
      Transforms.insertNodes(editor, content, { at: listItemNodePath });
      HelptapEditor.select(editor, listItemNodePath);
      return true;
    }
    return false;
  } else {
    const listItemNodePath = Path.parent(topLevelNodePath);
    const listItemNode = Node.get(editor, listItemNodePath);
    Transforms.removeNodes(editor, { at: listItemNodePath });
    const bodyNode = listItemNode.children[1];
    if (HelptapEditor.isEmptyV2(editor, bodyNode) === false) {
      const content = structuredClone(bodyNode.children);
      Transforms.insertNodes(editor, content, { at: listItemNodePath });
    }
    HelptapEditor.paragraph.insert(editor, listItemNodePath);
  }

  return true;

}

export function toggle(editor, options, editorOptions, currentNodePath) {

  Editor.withoutNormalizing(editor, () => {
    Transforms.select(editor, currentNodePath);
    doInsert(editor, currentNodePath);
    Transforms.removeNodes(editor, { at: Path.next(currentNodePath) });
  });

}

export function duplicate(editor, node, nodePath) {

  const clonedNode = HelptapEditor.cloneNode(node);

  HelptapEditor.insertNode(editor, clonedNode, nodePath);

}

export function appendNode(editor, listItemNodePath, targetNodePath) {

  const listItemNode = Node.get(editor, listItemNodePath);
  const targetPath = [...listItemNodePath, 1, listItemNode.children[1].children.length];

  Editor.withoutNormalizing(editor, () => {
    Transforms.moveNodes(editor, {
      at: targetNodePath,
      to: targetPath
    });

    Transforms.setNodes(editor, {
      properties: {
        collapsed: false
      }
    }, { at: [...listItemNodePath, 0] });

    Transforms.setNodes(editor, {
      properties: {
        collapsed: false
      }
    }, { at: [...listItemNodePath, 1] });
    Transforms.select(editor, [...targetPath]);
    ReactEditor.focus(editor);
    Editor.normalize(editor, { force: true });
  });

  return targetPath;

}

export function handleTabKeyEvent(editor, editableNode, editableNodePath) {

  const listItemNodePath = Path.parent(editableNodePath);

  if (Path.hasPrevious(listItemNodePath) === false) {
    return false;
  }

  const previousNodePath = Path.previous(listItemNodePath);
  const previousNode = Node.get(editor, previousNodePath);

  if (previousNode.type !== elementTypes.TOGGLE_LIST_ITEM) {
    return false;
  }

  let targetPath = [...previousNodePath, 1, previousNode.children[1].children.length];

  Editor.withoutNormalizing(editor, () => {
    const previousBodyNode = previousNode.children[1];
    if (HelptapEditor.isEmptyV2(editor, previousBodyNode) === true && previousBodyNode.children.length === 1) {
      Transforms.removeNodes(editor, { at: [...previousNodePath, 1, 0] });
      targetPath = Path.previous(targetPath);
    }

    Transforms.moveNodes(editor, {
      at: listItemNodePath,
      to: targetPath
    });

    Transforms.setNodes(editor, {
      properties: {
        collapsed: false
      }
    }, { at: [...previousNodePath, 0] });

    Transforms.setNodes(editor, {
      properties: {
        collapsed: false
      }
    }, { at: [...previousNodePath, 1] });

  });
  Editor.normalize(editor, { force: true });

  select(editor, targetPath);

  return true;

}

export function expandParents(editor, path) {
  let currentPath = path;

  Editor.withoutNormalizing(editor, () => {
    while (currentPath.length > 0) {
      const node = Node.get(editor, currentPath);
      if (node.type === elementTypes.TOGGLE_LIST_ITEM) {
        Transforms.setNodes(editor, { properties: { collapsed: false } }, { at: [...currentPath, 0] });
        Transforms.setNodes(editor, { properties: { collapsed: false } }, { at: [...currentPath, 1] });
      }
      currentPath = Path.parent(currentPath);
    }
  });

}

/**
 * 
 * If immediate parent is not BODY, do nothing
 * If path is not the first child, do nothing
 * Select the parent node
 * Collapse the parent node
 * 
 * */
export function trySelectHead(editor, path) {

  let parentPath = Path.parent(path);

  const node = Node.get(editor, parentPath);

  if (node.type !== elementTypes.TOGGLE_LIST_BODY) {
    return;
  }

  if (path[path.length - 1] !== 0) {
    return;
  }

  const listItemNodePath = Path.parent(parentPath);
  select(editor, listItemNodePath);
  toggleCollapsed(editor, Path.previous(parentPath));

}